import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
// eslint-disable-next-line no-restricted-imports -- This is the one place we must import the MUI MenuItem component
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectProps } from '@mui/material/Select';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import type { FormValues, HelperText } from '../utils/formUtils';

export type MenuOption<T extends string | number> = { label?: string; value: T };

type SelectionMenuProps<T extends string | number> = Pick<
  SelectProps,
  | 'aria-label'
  | 'defaultValue'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'renderValue'
  | 'value'
> & {
  helperText?: HelperText<FormValues>;
  options: MenuOption<T>[];
};

type DateSelectionMenuProps<T extends string | number> = Pick<SelectProps, 'defaultValue' | 'onChange'> & {
  options: MenuOption<T>[];
};

const DateSelect = styled(Select)`
  .MuiInput-input {
    padding-bottom: 0;
  }
`;

export function SelectionMenu<T extends string | number>({
  'aria-label': ariaLabel,
  defaultValue,
  disabled,
  error,
  fullWidth,
  helperText,
  name,
  onBlur,
  onChange,
  options,
  renderValue,
  value,
}: SelectionMenuProps<T>) {
  return (
    <FormControl fullWidth={fullWidth !== false}>
      <Select
        aria-label={ariaLabel}
        defaultValue={defaultValue}
        disabled={disabled}
        error={error}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        renderValue={renderValue}
        size="small"
        value={value}
        variant="outlined"
      >
        {options.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem key={optionValue} value={optionValue}>
            {optionLabel ?? optionValue}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
}

export function DateSelectionMenu<T extends string | number>({
  defaultValue,
  onChange,
  options,
}: DateSelectionMenuProps<T>) {
  const theme = useTheme();

  return (
    <FormControl fullWidth={false}>
      <DateSelect
        defaultValue={defaultValue}
        disableUnderline
        onChange={onChange}
        size="small"
        sx={{ fontSize: theme.typography.fontSize }}
        variant="standard"
      >
        {options.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem key={optionValue} sx={{ fontSize: theme.typography.fontSize }} value={optionValue}>
            {optionLabel ?? optionValue}
          </MenuItem>
        ))}
      </DateSelect>
    </FormControl>
  );
}
